<template lang="pug">
  .b-spec-request(data-testid="request-dialog")
    ._title Запросить КП
    ._columns
      ._items
        ._subtitle Позиции ({{ allowedItems.length }} из {{ items.length }})
        ._group(v-for="group in groups" :key="group.category")
          ._group-title {{ group.title }}
          ._item(v-for="item in group.items" :key="item.id" data-testid="request-item" :class="{ blocked: isBlocked(item) }")
            span._item-name(data-testid="request-item-name") {{ item.name }}
            span._item-blocked(v-if="isBlocked(item)" data-testid="request-item-blocked") запрос невозможен: позиция у заказчика или в закупке

      ._suppliers
        ._subtitle Поставщики
        ._filters
          BaseInput._filter(inputTestId="request-filter-region" v-model="region" label="регион" @input="reload")
          BaseInput._filter(inputTestId="request-filter-brand" v-model="brand" label="бренд" @input="reload")
          BaseInput._filter(inputTestId="request-search" v-model="search" label="поиск по названию" @input="reload")
        ._empty(v-if="!suppliers.length") Подходящих поставщиков нет
        ._supplier(v-for="supplier in suppliers" :key="supplier.id" data-testid="request-supplier")
          input._supplier-check(
            type="checkbox"
            data-testid="request-supplier-checkbox"
            :checked="selected.includes(supplier.id)"
            @change="toggle(supplier, $event.target.checked)"
            )
          span._supplier-name(data-testid="request-supplier-name") {{ supplier.name }}
          span._supplier-info {{ supplier.region }} · {{ categoryNames(supplier) }} · {{ (supplier.brands || []).join(', ') }}
          span._supplier-matching(data-testid="request-supplier-matching") подходящих позиций: {{ supplier.matchingItemsCount }}

    label._budget
      input(type="checkbox" data-testid="request-show-budget" v-model="showBudget")
      |
      | Показывать поставщику плановый бюджет

    ._result(v-if="result" data-testid="request-result") {{ resultText }}

    ._buttons
      Btn(blue data-testid="request-send" :disabled="!canSend" @click="send") Отправить запросы
      Btn(grey data-testid="request-close" @click="$emit('finish', sent)") Закрыть
</template>

<script>
import BaseInput from '@/components/BaseInput.vue';
import specificationApi from '@/api/specification';
import { apiRun } from '@/model/apiRun';
import { CATEGORIES, categoryTitle, BLOCKED_FOR_REQUEST } from '@/utils/spec';

/**
 * Окно «Запросить КП» (ТЗ 6.2, 4.1): выбранные позиции, сгруппированные по категориям (позиции,
 * ушедшие заказчику или в закупку, помечены и в запрос не передаются), подбор поставщиков с
 * фильтрами и переключатель показа планового бюджета.
 *
 * Окно не закрывается после отправки: по ТЗ 6.2 в нём показывается сообщение
 * «Отправлено: N новых запросов, M дополнены» и перечень пропущенных поставщиков.
 * Сгенерировано Claude.
 */
export default {
  name: 'SpecRequestPopupWrappable',
  components: { BaseInput },
  data() {
    return {
      specificationId: null,
      items: [],
      suppliers: [],
      selected: [],
      region: '',
      brand: '',
      search: '',
      showBudget: true,
      result: null,
      sent: false,
      busy: false,
    };
  },
  computed: {
    allowedItems() {
      return this.items.filter((item) => !this.isBlocked(item));
    },
    groups() {
      return Object.keys(CATEGORIES)
        .map((category) => ({
          category,
          title: categoryTitle(category),
          items: this.items.filter((item) => item.category === category),
        }))
        .filter((group) => group.items.length > 0);
    },
    canSend() {
      return !this.busy && this.allowedItems.length > 0 && this.selected.length > 0;
    },
    resultText() {
      if (!this.result) return '';
      const skipped = (this.result.skipped ?? [])
        .map((row) => row.name ?? row)
        .join(', ');
      const base = `Отправлено: ${this.result.created} новых запросов, ${this.result.extended} дополнены`;
      return skipped ? `${base}. Пропущены: ${skipped}` : base;
    },
  },
  methods: {
    init(params = {}) {
      this.specificationId = params.specificationId ?? null;
      this.items = params.items ?? [];
      this.region = params.region ?? '';
      this.reload();
    },
    isBlocked(item) {
      return BLOCKED_FOR_REQUEST.includes(item.status);
    },
    categoryNames(supplier) {
      return (supplier.categories ?? []).map(categoryTitle).join(', ');
    },
    reload() {
      if (!this.specificationId) return;
      const params = {
        items: this.allowedItems.map((item) => item.id),
        region: this.region,
        brand: this.brand,
        search: this.search,
      };
      specificationApi.fetchRequestSuppliers(this.specificationId, params)
        .then((r) => { this.suppliers = r.items ?? []; });
    },
    toggle(supplier, value) {
      this.selected = value
        ? [...this.selected, supplier.id]
        : this.selected.filter((id) => id !== supplier.id);
    },
    async send() {
      if (!this.canSend) return;
      this.busy = true;
      const result = await apiRun(
        () => specificationApi.createRequests(this.specificationId, {
          items: this.allowedItems.map((item) => item.id),
          suppliers: this.selected,
          showBudget: this.showBudget,
        }),
        'Не удалось отправить запросы',
      );
      this.busy = false;
      if (!result) return;
      this.result = result;
      this.sent = true;
      this.selected = [];
    },
  },
};
</script>

<style scoped lang="scss">
.spec-request {
  width: 900px;
  max-width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 14px;

  &__title {
    font-size: 24px;
    font-weight: 700;
  }

  &__columns {
    display: flex;
    gap: 24px;
    align-items: flex-start;
  }

  &__items,
  &__suppliers {
    flex: 1 1 0;
    min-width: 0;
    max-height: 50vh;
    overflow-y: auto;
  }

  &__subtitle {
    font-weight: 700;
    margin-bottom: 8px;
  }

  &__group-title {
    margin-top: 8px;
    font-size: 12px;
    color: var(--color-middle-gray);
  }

  &__item {
    display: flex;
    flex-direction: column;
    padding: 4px 0;
    font-size: 13px;

    &.blocked {
      color: var(--color-light-dark);
    }
  }

  &__item-blocked {
    font-size: 11px;
    color: var(--color-orange);
  }

  &__filters {
    display: flex;
    gap: 8px;
    margin-bottom: 10px;
  }

  &__filter {
    flex: 1 1 0;
    min-width: 0;
  }

  &__supplier {
    display: grid;
    grid-template-columns: 20px 1fr;
    gap: 2px 8px;
    padding: 6px 0;
    border-bottom: 1px solid var(--color-super-light);
    font-size: 13px;
  }

  &__supplier-check {
    width: 16px;
    height: 16px;
    margin: 0;
  }

  &__supplier-name {
    font-weight: 700;
  }

  &__supplier-info,
  &__supplier-matching {
    grid-column: 2;
    font-size: 11px;
    color: var(--color-middle-gray);
  }

  &__budget {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    cursor: pointer;
  }

  &__result {
    padding: 8px 12px;
    border-radius: 6px;
    background: #EEF6FF;
    font-size: 13px;
  }

  &__buttons {
    display: flex;
    gap: 12px;
  }

  &__empty {
    color: var(--color-middle-gray);
    font-size: 13px;
  }
}
</style>
